import React from "react";
import { Link } from "react-router-dom";
import {
  formatDate,
  formatDateTime,
  formatPhoneNumber,
} from "../../utils/formatters";
import type { Contact } from "../../types/contact.types";
import type { Organization } from "../../types/organization.types";
import EmailSuppressionBadge from "./EmailSuppressionBadge";

interface ContactPersonalSectionProps {
  contact: Contact;
  organization?: Organization | null;
}

function FieldRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (!value) {
    return null;
  }
  return (
    <div>
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-gray-900">{value}</dd>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
      {children}
    </h3>
  );
}

const STATUS_COLORS: Record<string, string> = {
  active: "bg-green-100 text-green-800",
  inactive: "bg-gray-100 text-gray-700",
  cancelled: "bg-red-100 text-red-800",
  suspended: "bg-yellow-100 text-yellow-800",
  pending: "bg-blue-100 text-blue-800",
};

const ContactPersonalSection: React.FC<ContactPersonalSectionProps> = ({
  contact,
  organization,
}) => {
  const displayPhone =
    contact.mobile_phone || contact.phone_number || contact.phone;
  const hasAddress =
    contact.address_line1 ||
    contact.city ||
    contact.state ||
    contact.postal_code;
  const hasMemberData =
    contact.is_usls_member ||
    contact.member_status ||
    contact.membership_id ||
    contact.zuora_account_id;
  let contactTypes: string[] = [];
  if (contact.contact_types?.length) {
    contactTypes = contact.contact_types;
  } else if (contact.contact_type) {
    contactTypes = [contact.contact_type];
  }

  return (
    <div className="space-y-6">
      {/* Contact Details */}
      <div>
        <SectionTitle>Contact Details</SectionTitle>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          {contact.email && (
            <FieldRow
              label="Email"
              value={
                <div className="flex items-center gap-2 flex-wrap">
                  <a
                    href={`mailto:${contact.email}`}
                    className="text-primary-600 hover:underline"
                  >
                    {contact.email}
                  </a>
                  <EmailSuppressionBadge
                    email={contact.email}
                    isEmailSuppressed={contact.is_email_suppressed}
                    suppressionReason={contact.email_suppression_reason}
                    suppressionDetail={contact.email_suppression_detail}
                    suppressionUpdatedAt={contact.email_suppression_updated_at}
                  />
                </div>
              }
            />
          )}
          {displayPhone && (
            <FieldRow
              label="Mobile / Cell"
              value={formatPhoneNumber(displayPhone)}
            />
          )}
          {contact.work_email && (
            <FieldRow
              label="Work Email"
              value={
                <a
                  href={`mailto:${contact.work_email}`}
                  className="text-primary-600 hover:underline"
                >
                  {contact.work_email}
                </a>
              }
            />
          )}
          {contact.work_phone && (
            <FieldRow
              label="Work Phone"
              value={formatPhoneNumber(contact.work_phone)}
            />
          )}
          {contact.personal_email && (
            <FieldRow
              label="Personal Email"
              value={
                <a
                  href={`mailto:${contact.personal_email}`}
                  className="text-primary-600 hover:underline"
                >
                  {contact.personal_email}
                </a>
              }
            />
          )}
          {contact.home_phone && (
            <FieldRow
              label="Home Phone"
              value={formatPhoneNumber(contact.home_phone)}
            />
          )}
          {contact.date_of_birth && (
            <FieldRow
              label="Date of Birth"
              value={formatDate(contact.date_of_birth)}
            />
          )}
          {organization && (
            <FieldRow
              label="Organization"
              value={
                <Link
                  to={`/organizations/${organization.id}`}
                  className="text-primary-600 hover:underline"
                >
                  {organization.name}
                </Link>
              }
            />
          )}
          {contact.lifecycle_stage && (
            <FieldRow
              label="Lifecycle Stage"
              value={
                <span className="capitalize">{contact.lifecycle_stage}</span>
              }
            />
          )}
          {contact.source && (
            <FieldRow
              label="Source"
              value={<span className="capitalize">{contact.source}</span>}
            />
          )}
          {contact.acquisition_source && (
            <FieldRow
              label="Acquisition Source"
              value={contact.acquisition_source}
            />
          )}
          {contact.retailer && (
            <FieldRow label="Retailer" value={contact.retailer} />
          )}
          {contact.last_activity_at && (
            <FieldRow
              label="Last Activity At"
              value={formatDateTime(contact.last_activity_at)}
            />
          )}
        </dl>
        {contactTypes.length > 0 && (
          <div className="mt-4">
            <dt className="text-sm font-medium text-gray-500 mb-1">
              Contact Types
            </dt>
            <dd className="flex flex-wrap gap-1">
              {contactTypes.map((t) => (
                <span
                  key={t}
                  className="px-2 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-full"
                >
                  {t}
                </span>
              ))}
            </dd>
          </div>
        )}
      </div>

      {/* Checkout Progress (prospect-create API fields). Shown only when there's
          meaningful signal — not for every contact whose cart_abandoned is the
          default false. */}
      {(contact.furthest_step_reached ||
        contact.last_date_visited ||
        contact.cart_abandoned === true) && (
        <div className="border-t border-gray-100 pt-4">
          <SectionTitle>Checkout Progress</SectionTitle>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            {contact.furthest_step_reached && (
              <FieldRow
                label="Furthest Step Reached"
                value={contact.furthest_step_reached}
              />
            )}
            {contact.last_date_visited && (
              <FieldRow
                label="Last Date Visited"
                value={formatDateTime(contact.last_date_visited)}
              />
            )}
            <FieldRow
              label="Cart Abandoned"
              value={
                <span
                  className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                    contact.cart_abandoned
                      ? "bg-amber-100 text-amber-800"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {contact.cart_abandoned ? "Yes" : "No"}
                </span>
              }
            />
          </dl>
        </div>
      )}

      {/* Address */}
      {hasAddress && (
        <div className="border-t border-gray-100 pt-4">
          <SectionTitle>Address</SectionTitle>
          <address className="not-italic text-sm text-gray-900 space-y-0.5">
            {contact.address_line1 && <div>{contact.address_line1}</div>}
            {contact.address_line2 && <div>{contact.address_line2}</div>}
            {(contact.city || contact.state || contact.postal_code) && (
              <div>
                {[contact.city, contact.state, contact.postal_code]
                  .filter(Boolean)
                  .join(", ")}
              </div>
            )}
            {contact.country && <div>{contact.country}</div>}
          </address>
        </div>
      )}

      {/* Membership IDs */}
      {hasMemberData && (
        <div className="border-t border-gray-100 pt-4">
          <SectionTitle>Membership</SectionTitle>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            {contact.member_status && (
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  Member Status
                </dt>
                <dd className="mt-0.5 flex items-center gap-2">
                  <span
                    className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                      STATUS_COLORS[contact.member_status.toLowerCase()] ??
                      "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {contact.member_status}
                  </span>
                  {contact.is_primary_member !== undefined &&
                    contact.is_primary_member !== null && (
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                          contact.is_primary_member
                            ? "bg-purple-100 text-purple-800"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {contact.is_primary_member ? "Primary" : "Secondary"}
                      </span>
                    )}
                </dd>
              </div>
            )}
            {contact.membership_type && (
              <FieldRow
                label="Membership Type"
                value={contact.membership_type}
              />
            )}
            {contact.membership_id && (
              <FieldRow label="Member Number" value={contact.membership_id} />
            )}
            {contact.zuora_account_id && (
              <FieldRow
                label="Zuora Account ID"
                value={contact.zuora_account_id}
              />
            )}
            {contact.member_start_date && (
              <FieldRow
                label="Member Since"
                value={formatDate(contact.member_start_date)}
              />
            )}
            {contact.member_end_date && (
              <FieldRow
                label="End Date"
                value={formatDate(contact.member_end_date)}
              />
            )}
            {contact.member_renewal_date && (
              <FieldRow
                label="Renewal Date"
                value={formatDate(contact.member_renewal_date)}
              />
            )}
            {contact.last_invoice_date && (
              <FieldRow
                label="Last Invoice Date"
                value={formatDate(contact.last_invoice_date)}
              />
            )}
            {contact.cancellation_reason && (
              <div className="md:col-span-2">
                <dt className="text-sm font-medium text-gray-500">
                  Cancellation Reason
                </dt>
                <dd className="mt-0.5 text-sm text-red-700">
                  {contact.cancellation_reason}
                </dd>
              </div>
            )}
            {contact.zuora_last_synced_at && (
              <FieldRow
                label="Last Synced"
                value={formatDate(contact.zuora_last_synced_at)}
              />
            )}
          </dl>
        </div>
      )}
    </div>
  );
};

export default ContactPersonalSection;
